import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { Play, Coins, ArrowRight, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { TrackCard } from '../components/TrackCard';
import { AlbumCard } from '../components/AlbumCard';
import { MerchCard } from '../components/MerchCard';
import { SubscribeForm } from '../components/SubscribeForm';
import { ReleaseCountdown } from '../components/ReleaseCountdown';
import { WaitlistWidget } from '../components/WaitlistWidget';
import { ReferralLeaderboard } from '../components/ReferralLeaderboard';
import { VotingWidget } from '../components/VotingWidget';
import { FanWall } from '../components/FanWall';
import { usePlayer } from '../context/PlayerContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function HomePage() {
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [merch, setMerch] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduledTracks, setScheduledTracks] = useState([]);
  const { playTrack, playPreview } = usePlayer();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [albumsRes, tracksRes, merchRes, settingsRes, upcomingRes] = await Promise.all([
          axios.get(`${API_URL}/albums`),
          axios.get(`${API_URL}/tracks`),
          axios.get(`${API_URL}/merch`),
          axios.get(`${API_URL}/settings`),
          axios.get(`${API_URL}/tracks/upcoming`)
        ]);
        
        setAlbums(albumsRes.data);
        setTracks(tracksRes.data);
        setMerch(merchRes.data);
        setSettings(settingsRes.data);
        setScheduledTracks(upcomingRes.data);
      } catch (error) {
        logger.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featuredTrack = tracks[0];
  const featuredAlbum = settings?.featured_album_id 
    ? albums.find(a => a.id === settings.featured_album_id) || albums[0]
    : albums[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content" data-testid="home-page">
      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center hero-gradient ${settings?.announcement_enabled && settings?.announcement_text ? 'pt-12' : ''}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container relative z-10 pt-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <p className="text-cyan-400 font-medium mb-4 subheading tracking-wider">
                {settings?.artist_tagline || 'ARTIST • PRODUCER • VISIONARY'}
              </p>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
                <span className="gradient-text">
                  {(settings?.hero_title || '@DaRealQJones').split('').slice(0, Math.ceil((settings?.hero_title || '@DaRealQJones').length / 2)).join('')}
                </span>
                {(settings?.hero_title || '@DaRealQJones').split('').slice(Math.ceil((settings?.hero_title || '@DaRealQJones').length / 2)).join('')}
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-lg">
                {settings?.hero_description || 'Experience music at the edge of reality. Stream exclusive tracks, own fractional tokens, and get limited merch drops.'}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => featuredTrack && playTrack(featuredTrack, tracks)}
                  className="btn-primary flex items-center gap-2 animate-pulse-glow"
                  data-testid="hero-play-button"
                >
                  <Play className="w-5 h-5" />
                  Play Latest
                </button>
                <Link to="/tokens" className="btn-secondary flex items-center gap-2" data-testid="hero-tokens-button">
                  <Coins className="w-5 h-5" />
                  Own Music Tokens
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/10">
                <div>
                  <p className="text-3xl font-bold gradient-text">{tracks.length}</p>
                  <p className="text-sm text-gray-500">Tracks</p>
                </div>
                <div>
                  <p className="text-3xl font-bold gradient-text">{albums.length}</p>
                  <p className="text-sm text-gray-500">Albums</p>
                </div>
                <div>
                  <p className="text-3xl font-bold gradient-text">{merch.length}</p>
                  <p className="text-sm text-gray-500">Merch Items</p>
                </div>
              </div>
            </div>

            {/* Featured Album */}
            {featuredAlbum && (
              <div className="animate-slide-up stagger-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-2xl blur-xl" />
                  <img 
                    src={featuredAlbum.cover_url}
                    alt={featuredAlbum.title}
                    className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl"
                    data-testid="featured-album-cover"
                  />
                  <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4">
                    <p className="text-sm text-cyan-400 mb-1">Latest Release</p>
                    <h3 className="text-xl font-bold">{featuredAlbum.title}</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Release Countdown Section - Shows upcoming scheduled tracks */}
      {scheduledTracks.length > 0 && (
        <section className="py-16 px-4 relative overflow-hidden" data-testid="countdown-section">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-cyan-500/5 to-purple-600/10" />
          <div className="container relative z-10">
            <ReleaseCountdown 
              releaseDate={scheduledTracks[0].release_date}
              title={scheduledTracks[0].title}
              coverUrl={scheduledTracks[0].cover_url}
              onPreview={scheduledTracks[0].audio_url ? () => playPreview(scheduledTracks[0], 30) : null}
              variant="hero"
            />
          </div>
        </section>
      )}

      {/* Fan Waitlist - First-Listen access */}
      <WaitlistWidget />

      {/* Referral Leaderboard - top fans competing for bonus */}
      <ReferralLeaderboard />

      {/* Fan Playlist Voting - weekly snippet vote */}
      <VotingWidget />

      {/* Fan Wall - live reactions across all rounds */}
      <FanWall />

      {/* Featured Tracks */}
      <section className="py-24" data-testid="featured-tracks-section">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Featured Tracks</h2>
              <p className="text-gray-400">Stream the latest releases</p>
            </div>
            <Link to="/music" className="btn-ghost flex items-center gap-1 text-purple-400" data-testid="view-all-music">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {tracks.slice(0, 4).map((track, i) => (
              <div key={track.id} className={`animate-fade-in stagger-${i + 1}`}>
                <TrackCard track={track} tracks={tracks} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tokenization CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-cyan-900/20" />
        <div className="container relative z-10">
          <div className="glass-card p-8 md:p-12 max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mx-auto mb-6">
              <Coins className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Own A Piece of the Music</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Fractional ownership tokens let you invest in your favorite tracks. 
              As the music earns, token holders share in the success.
            </p>
            <Link to="/tokens" className="btn-primary inline-flex items-center gap-2" data-testid="explore-tokens-cta">
              Explore Token Marketplace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Albums */}
      <section className="py-24" data-testid="albums-section">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Albums</h2>
              <p className="text-gray-400">Full listening experiences</p>
            </div>
            <Link to="/music" className="btn-ghost flex items-center gap-1 text-purple-400" data-testid="view-all-albums">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="album-grid">
            {albums.map((album, i) => (
              <div key={album.id} className={`animate-fade-in stagger-${i + 1}`}>
                <AlbumCard album={album} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Merch */}
      <section className="py-24" data-testid="merch-section">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Merch</h2>
              <p className="text-gray-400">Limited edition drops</p>
            </div>
            <Link to="/merch" className="btn-ghost flex items-center gap-1 text-purple-400" data-testid="view-all-merch">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="merch-grid">
            {merch.slice(0, 3).map((item, i) => (
              <div key={item.id} className={`animate-fade-in stagger-${i + 1}`}>
                <MerchCard item={item} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-24 relative overflow-hidden" data-testid="subscribe-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" />
        <div className="container relative z-10">
          <div className="max-w-md mx-auto">
            <SubscribeForm />
          </div>
        </div>
      </section>
    </div>
  );
}
